import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { v4 as uuidv4 } from 'uuid'
import { BillingAddressInput, Callback } from 'lib/types'
import { Slice, BillingAddressSlice } from './types'
import { setCart } from '../cart'
import { PWAThunk } from 'lib/core/data'

export const initialState: Slice = {}

interface ActionData {
  cartId: string
  uuid: string
}

const getSlice = (slice: BillingAddressSlice, uuid?: string): BillingAddressSlice => {
  return slice ?? ({
    uuid,
    isFetching: false,
    isSuccess: false,
    isFailure: false
  })
}

const slice = createSlice({
  name: 'quote/setBillingAddress',
  initialState,
  reducers: {
    request (state, action: PayloadAction<ActionData>) {
      const { cartId, uuid } = action.payload
      const slice = getSlice(state[cartId], uuid)
      slice.uuid = uuid
      slice.isFetching = true
      state[cartId] = slice
    },

    success (state, action: PayloadAction<ActionData>) {
      const { cartId, uuid } = action.payload
      const slice = getSlice(state[cartId])
      if (uuid === slice.uuid) {
        slice.isFetching = false
        slice.isSuccess = true
        slice.isFailure = false
      }
    },

    failure (state, action: PayloadAction<ActionData>) {
      const { cartId, uuid } = action.payload
      const slice = getSlice(state[cartId])
      if (uuid === slice.uuid) {
        slice.isFetching = false
        slice.isSuccess = false
        slice.isFailure = true
      }
    }
  }
})

export const reducer = slice.reducer

export const { request, success, failure } = slice.actions

export interface SetBillingAddressParams { // TODO: this interface is used in many places. Find a way to re-use
  address?: BillingAddressInput
  addressId?: number
  sameAsShipping?: boolean
}

export function setBillingAddress (cartId: string, actionParams: SetBillingAddressParams, callback?: Callback): PWAThunk {
  return async (dispatch, getState, params) => {
    const { magentoClient } = params
    const uuid = uuidv4()
    dispatch(request({ cartId, uuid }))
    try {
      const {
        address,
        addressId,
        sameAsShipping,
        ...restInputParams
      } = actionParams

      const response = await magentoClient.getClient().quote.setBillingAddresses({
        cartId,
        address,
        addressId,
        sameAsShipping,
        ...restInputParams
      })

      const { data } = response
      const { setBillingAddressOnCart } = data
      const { cart } = setBillingAddressOnCart

      if (cart) {
        dispatch(setCart({
          cartId,
          data: cart,
          merge: true
        }))
        dispatch(success({ cartId, uuid }))
        callback?.onSuccess?.()
      } else {
        dispatch(failure({ cartId, uuid }))
        callback?.onFailure?.({
          error: new Error(`[Billing Address] Unexpected payload: ${JSON.stringify(response)}`),
          extraData: {
            cartId,
            address: actionParams?.address,
            addressId: actionParams?.addressId,
            sameAsShipping: actionParams?.sameAsShipping
          }
        })
      }

      return response
    } catch (e) {
      dispatch(failure({ cartId, uuid }))
      callback?.onFailure?.({
        error: new Error(`[Shipping Address] ${e.message}`),
        extraData: {
          cartId,
          address: actionParams?.address,
          addressId: actionParams?.addressId,
          sameAsShipping: actionParams?.sameAsShipping
        }
      })
    }
  }
}
